import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { TLearnerProgressInfo } from "@/features/learning-progress/types/learnerDetail";
import { divider, black, text } from "@/theme/colors";
import { EMPTY_STR } from "@/constants/string";
import { Stack, Grid, Typography } from "@mui/material";
import { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

type TProps = {
  data: TLearnerProgressInfo;
};

const InfoItem: FC<{
  label: string;
  value?: string | null;
  isLink?: boolean;
  onClick?: () => void;
}> = ({ label, value, isLink, onClick }) => (
  <Stack gap={1} sx={{ pb: 2, borderBottom: `1px solid ${divider.middle}` }}>
    <Typography variant="cap12" color={black[70]}>
      {label}
    </Typography>
    <Typography
      variant="body14Semi"
      color={isLink ? "#185DE3" : text.primary}
      onClick={isLink ? onClick : undefined}
      sx={{ wordBreak: "break-word", cursor: isLink ? "pointer" : "default", textDecoration: isLink ? "underline" : "none" }}
    >
      {value || EMPTY_STR.TEXT}
    </Typography>
  </Stack>
);

const LEARNER_ACCOUNT_SECTION_HASH = encodeURIComponent("学習アプリのアカウント管理");

export const LearnerInfoReadOnlySection: FC<TProps> = ({ data }) => {
  const navigate = useNavigate();
  const { learnerId: learnerIdParam } = useParams();

  const handleGoToLearnerDetail = () => {
    const learnerId = learnerIdParam ?? String(data.id);
    navigate(getDynamicRoute(AppRoutes.learnerDetail, { learnerId }));
  };

  const handleGoToLearnerAccount = () => {
    const learnerId = learnerIdParam ?? String(data.id);
    navigate(`${getDynamicRoute(AppRoutes.learnerDetail, { learnerId })}#${LEARNER_ACCOUNT_SECTION_HASH}`);
  };

  return (
    <InnerBox
      p={3}
      showDivider={false}
      borderColor={divider.high}
      bordered
      title="外国人情報"
      rightContent={
        <Stack direction="row" gap={2}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGoToLearnerDetail}
            startIcon={<PersonOutlineIcon sx={{ fontSize: 20 }} />}
          >
            外国人の詳細
          </Button>
        </Stack>
      }
    >
      <Stack gap={2} mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <InfoItem label="氏名" value={data.fullName} />
          </Grid>
          <Grid item xs={6}>
            <InfoItem label="呼び名" value={data.nickname} />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <InfoItem label="受験ステータス" value={data.examStatus} />
          </Grid>
          <Grid item xs={6}>
            <InfoItem
              label="学習アプリのアカウント"
              value={data.learnerAccountText}
              isLink={data.learnerAccountLink}
              onClick={handleGoToLearnerAccount}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <InfoItem label="カリキュラム開始日" value={data.curriculumStartDate} />
          </Grid>
          <Grid item xs={6}>
            <InfoItem label="受験予定年月" value={data.examScheduleYearMonth} />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <InfoItem label="受講可能な週数" value={`${data.availableWeeks} 週間`} />
          </Grid>
          <Grid item xs={6}>
            <InfoItem label="1号残期間" value={data.remainingPeriod} />
          </Grid>
        </Grid>

        <Stack gap={1}>
          <Typography variant="cap12" color={black[70]}>
            メモ
          </Typography>
          <Typography variant="body14Semi" color={text.primary}>
            {data.memo || EMPTY_STR.TEXT}
          </Typography>
        </Stack>
      </Stack>
    </InnerBox>
  );
};
